/**
 * Leaderboard client. Talks to the API Gateway endpoint provisioned by
 * packages/infra. The endpoint URL is baked in at build time via Vite env so
 * it ships with the bundle.
 */

export const API_ENDPOINT =
  (import.meta.env?.VITE_LEADERBOARD_URL as string | undefined) ||
  'https://hl9hc6pwtf.execute-api.us-east-1.amazonaws.com';

const ENDPOINT = API_ENDPOINT;

const NAME_KEY = 'shmerge.playerName';
const NAME_CUSTOMIZED_KEY = 'shmerge.playerNameCustomized';

export type LeaderboardEntry = { playerName: string; score: number; ts: number };

export function getPlayerName(): string {
  try {
    return localStorage.getItem(NAME_KEY) || '';
  } catch {
    return '';
  }
}

/** True once the player has explicitly chosen a name (vs the generated default). */
export function isPlayerNameCustomized(): boolean {
  try {
    return localStorage.getItem(NAME_CUSTOMIZED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist a name the player typed themselves. */
export function setPlayerName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name.slice(0, 16));
    localStorage.setItem(NAME_CUSTOMIZED_KEY, '1');
  } catch {}
}

/**
 * The player's leaderboard name, generating and persisting a default like
 * "Shmerger4821" on first use. Scores always submit without an input gate;
 * the player can tap to rename at any time (see GameOverScene).
 */
export function ensurePlayerName(): string {
  const existing = getPlayerName();
  if (existing) {
    // Names saved before the default-name flow existed were typed by the
    // player — treat them as customized so they don't get the rename nudge.
    try {
      if (localStorage.getItem(NAME_CUSTOMIZED_KEY) === null) {
        localStorage.setItem(NAME_CUSTOMIZED_KEY, '1');
      }
    } catch {}
    return existing;
  }
  const name = `Shmerger${1000 + Math.floor(Math.random() * 9000)}`;
  try {
    localStorage.setItem(NAME_KEY, name);
    // Explicit '0' so the legacy migration above can't mistake this generated
    // default for a player-typed name in a later session.
    localStorage.setItem(NAME_CUSTOMIZED_KEY, '0');
  } catch {}
  return name;
}

export async function fetchTop(limit = 20): Promise<LeaderboardEntry[]> {
  const res = await fetch(`${ENDPOINT}/scores?limit=${limit}`);
  if (!res.ok) throw new Error(`leaderboard fetch failed (${res.status})`);
  const data = (await res.json()) as { entries: LeaderboardEntry[] };
  return data.entries ?? [];
}

/**
 * Removes every score submitted under `playerName` from the global board.
 * Backs the account-deletion flow; throws on a non-OK response so the caller
 * can tell the player whether the server copy is actually gone.
 */
export async function deletePlayerScores(playerName: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/scores?playerName=${encodeURIComponent(playerName)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`score deletion failed (${res.status})`);
}

export async function submitScore(score: number, playerName: string): Promise<void> {
  if (!Number.isFinite(score) || score <= 0) return;
  const body = JSON.stringify({ score: Math.floor(score), playerName });
  await fetch(`${ENDPOINT}/scores`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
  });
}
